import { useState, useCallback } from "react";
import apiClient from "../../../../shared/apiClient";

interface AddressValidation {
  formattedAddress: string | null;
  addressValid: boolean;
}

export const useAddressValidationCache = () => {
  // Map of query to validated address
  const [validatedAddresses, setValidatedAddresses] = useState<
    Record<string, string>
  >({});

  const validateAddress = useCallback(
    async (input: string) => {
      const query = input.trim().toLowerCase();
      if (!query) return;

      // Don't validate the same query twice
      if (query in validatedAddresses) return;

      try {
        const response = await apiClient.post("/address/validate", {
          address: query,
        });
        const validation = response.data as AddressValidation;
        if (validation.addressValid && validation.formattedAddress) {
          // Only store non-null formatted addresses
          setValidatedAddresses((prev) => ({
            ...prev,
            [query]: validation.formattedAddress!,
          }));
        }
      } catch (err) {
        console.error("Failed to validate address:", err);
      }
    },
    [validatedAddresses]
  );

  const findMatchingValidatedAddress = useCallback(
    (query: string): string | null => {
      const lowerQuery = query.trim().toLowerCase();
      return (
        Object.values(validatedAddresses).find((address) =>
          address.toLowerCase().includes(lowerQuery)
        ) || null
      );
    },
    [validatedAddresses]
  );

  const isValidAddress = useCallback(
    (address: string) => {
      return Object.values(validatedAddresses).includes(address);
    },
    [validatedAddresses]
  );

  // Get unique addresses in a stable order
  const getValidatedAddresses = useCallback(() => {
    return Array.from(new Set(Object.values(validatedAddresses))).sort();
  }, [validatedAddresses]);

  return {
    validatedAddresses: getValidatedAddresses(),
    validateAddress,
    findMatchingValidatedAddress,
    isValidAddress,
  };
};
